// Reconstructs the front-end "/ogloszenia" list URL from a searchjoy `params_jsonb`
// document (the search engine `SearchQuery` shape), so a moderator can open exactly the
// list the client saw.
//
// Base URL is authoritative: the notification worker deep-links tenders with the same
// origin (best-biznes-search-engine, crates/notifications: `NOTIFY_APP_URL`, default
// `https://app.bestbiznes.pl`). Override in the panel via `VITE_APP_URL`.
//
// Mapping and value format are verified against the front-end parser:
//   - lib/screens/lista_ogloszen_container/lista_ogloszen_page.dart (@QueryParam names +
//     which params `initState` actually applies)
//   - lib/utils/helpers/filter/auction_filters_model.dart `toUrlQueryParams` (list values
//     are URL-encoded and joined by the literal '|' separator).
// Only params the front actually parses are emitted; `auction_type` and `budget` are
// intentionally skipped (their `initState` branches are commented out on the front).

export const APP_URL = import.meta.env.VITE_APP_URL || "https://app.bestbiznes.pl";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

// Non-empty string list from a params field; anything else → [].
function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (typeof entry === "string" ? entry : String(entry ?? "")))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0 && entry !== "*");
}

// Text phrases: `queries[]` when present, else the single `q`. Wildcard/blank dropped —
// mirrors SearchQuery::search_phrases.
function phrases(params: UnknownRecord): string[] {
  const fromQueries = stringList(params.queries);
  if (fromQueries.length > 0) return fromQueries;
  const q = typeof params.q === "string" ? params.q.trim() : "";
  return q.length > 0 && q !== "*" ? [q] : [];
}

function scalarString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number") return String(value);
  return null;
}

// SearchQuery list-field → front @QueryParam name. Values join with the literal '|'.
const LIST_PARAM_MAP: Array<[string, string]> = [
  ["form", "form"],
  ["category", "category"],
  ["industries", "industries"],
  ["branches", "branches"],
  ["cpv_code", "cpv_code"],
  ["states", "states"],
  ["districts", "districts"],
  ["cities", "cities"],
  ["country", "country"],
  ["emails", "emails"],
  ["phones", "phones"],
  ["id", "id"],
  ["source", "source"],
  ["organizer_name", "organizer_name"],
];

// SearchQuery scalar-field → front @QueryParam name.
const SCALAR_PARAM_MAP: Array<[string, string]> = [
  ["created_at_min", "created_at_min"],
  ["created_at_max", "created_at_max"],
  ["submitting_date_min", "submitting_date_min"],
  ["submitting_date_max", "submitting_date_max"],
  ["sort", "sort"],
  ["direction", "direction"],
  ["limit", "limit"],
];

// Presence booleans the front parses (permission-gated on its side; harmless to include).
const BOOL_PARAMS = ["wadium", "attachments", "original_link", "hide_untranslated"] as const;

/**
 * Build the absolute front-end list URL for a searchjoy `params_jsonb`. Returns the base
 * `/ogloszenia` (no query) for legacy docs without a params object — never null, so the
 * "open as user" action is available on every row.
 */
export function buildFrontFilterUrl(paramsJsonb: unknown): string {
  const base = `${APP_URL.replace(/\/+$/, "")}/ogloszenia`;
  const params = asRecord(paramsJsonb);
  if (!params) return base;

  const parts: string[] = [];
  const enc = encodeURIComponent;
  for (const phrase of stringList(params.excluded_phrases)) {
    parts.push(`excluded_phrases=${enc(phrase)}`);
  }

  const phraseList = phrases(params);
  if (phraseList.length > 0) parts.push(`q=${phraseList.map(enc).join("|")}`);

  for (const [source, target] of LIST_PARAM_MAP) {
    const values = stringList(params[source]);
    if (values.length > 0) parts.push(`${target}=${values.map(enc).join("|")}`);
  }

  for (const [source, target] of SCALAR_PARAM_MAP) {
    const value = scalarString(params[source]);
    if (value !== null) parts.push(`${target}=${enc(value)}`);
  }

  for (const key of BOOL_PARAMS) {
    if (typeof params[key] === "boolean") parts.push(`${key}=${String(params[key])}`);
  }

  return parts.length > 0 ? `${base}?${parts.join("&")}` : base;
}

/**
 * Absolute front-end tender URL for a conversion `auction_id` (a pretty_id). Same origin
 * and route as the notification deep links (`/ogloszenia/<pretty_id>`).
 */
export function buildAuctionUrl(auctionId: string | null | undefined): string | null {
  if (!auctionId) return null;
  return `${APP_URL.replace(/\/+$/, "")}/ogloszenia/${encodeURIComponent(auctionId)}`;
}
